import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/cn"
import { registerPasskey } from "@/lib/webauthn"
import type { SharedProps } from "@/types/inertia"

type PasskeyRow = {
  id: number
  nickname: string | null
  lastUsedAt: string | null
  createdAt: string
}

type Props = {
  mfaEnrolled:    boolean
  hasTotp:        boolean
  passkeys:       PasskeyRow[]
  recoveryCodes:  string[] | null
  passkeyOptions: Record<string, unknown> | null
  challenge:      string | null
}

const TABS = [
  { key: "passkey",       label: "Passkey" },
  { key: "authenticator", label: "Authenticator app" },
]

export default function MfaEnroll({ mfaEnrolled, hasTotp, passkeys, recoveryCodes, passkeyOptions }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash
  const [tab, setTab] = useState<"passkey" | "authenticator">("passkey")

  return (
    <AppShell>
      <PageHeader
        eyebrow="Security"
        title={mfaEnrolled ? "Manage second factors" : "Enable two-factor authentication"}
        subtitle="Pick at least one factor — passkey or authenticator app — and save your recovery codes."
      />

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      {recoveryCodes && <RecoveryCodesPanel codes={recoveryCodes} />}

      <section className="grid grid-cols-[220px_1fr] gap-5">
        <aside className="space-y-1">
          {TABS.map((t) => {
            const active = t.key === tab
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key as "passkey" | "authenticator")}
                className={cn(
                  "block w-full rounded-control px-3 py-2 text-left text-sm transition",
                  active
                    ? "border border-brand-red/30 bg-brand-redSoft text-app-text"
                    : "text-app-muted hover:bg-white/5",
                )}
              >
                {t.label}
              </button>
            )
          })}
        </aside>

        <div className="space-y-5">
          {tab === "passkey" && (
            <PasskeyTab passkeys={passkeys} options={passkeyOptions} />
          )}
          {tab === "authenticator" && (
            <AuthenticatorTab hasTotp={hasTotp} />
          )}

          <RegenerateRecoveryCard />
        </div>
      </section>
    </AppShell>
  )
}

function PasskeyTab({ passkeys, options }: { passkeys: PasskeyRow[], options: Record<string, unknown> | null }) {
  const [nickname, setNickname] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function add() {
    if (!options) {
      setError("Refresh the page to get a new challenge.")
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const credential = await registerPasskey(options as Parameters<typeof registerPasskey>[0])
      router.post("/portal/mfa/enroll/passkey", { credential, nickname })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Passkey</h2>
        <p className="text-sm text-app-muted">
          Use Touch ID, Windows Hello, a YubiKey, or a phone-as-roaming-authenticator. Strongest factor on the list.
        </p>
      </header>

      <div className="space-y-3">
        {passkeys.length === 0 && (
          <div className="rounded-control border border-border bg-white/5 px-3 py-2 text-sm text-app-muted">
            No passkeys yet. Add one to skip codes for the next sign-in.
          </div>
        )}
        {passkeys.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-control border border-border bg-white/5 px-3 py-2 text-sm">
            <div>
              <div className="text-app-text">{p.nickname || "Unnamed passkey"}</div>
              <div className="text-xs text-app-muted">
                Added {new Date(p.createdAt).toLocaleDateString()}{p.lastUsedAt ? ` · last used ${new Date(p.lastUsedAt).toLocaleDateString()}` : ""}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.delete(`/portal/mfa/passkeys/${p.id}`)}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        <label className="block text-sm">
          <span className="block text-app-muted">Nickname</span>
          <Input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Work laptop, YubiKey, etc."
          />
        </label>
        {error && <p className="text-sm text-status-error">{error}</p>}
        <Button onClick={add} disabled={submitting}>
          {submitting ? "Waiting for browser…" : "Add passkey"}
        </Button>
      </div>
    </Card>
  )
}

function AuthenticatorTab({ hasTotp }: { hasTotp: boolean }) {
  const [qrSvg, setQrSvg] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function start() {
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/portal/mfa/enroll/totp/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
      })
      if (!res.ok) throw new Error("Could not start TOTP enrollment.")
      const json = await res.json()
      setQrSvg(json.qr_svg as string)
      setSecret(json.secret as string)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  function verify(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    router.post("/portal/mfa/enroll/totp/verify", { code })
  }

  return (
    <Card className="p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Authenticator app</h2>
        <p className="text-sm text-app-muted">
          Scan with 1Password / Authy / Google Authenticator, then enter the 6-digit code to confirm.
        </p>
      </header>

      {hasTotp && !qrSvg && (
        <div className="mb-4 rounded-control border border-status-success/30 bg-status-success/10 px-3 py-2 text-sm text-status-success">
          TOTP is wired. You can re-pair below if you've lost access — re-pairing replaces the existing secret.
        </div>
      )}

      {!qrSvg && (
        <Button onClick={start} disabled={submitting}>
          {submitting ? "Generating…" : (hasTotp ? "Re-pair authenticator" : "Set up authenticator")}
        </Button>
      )}
      {error && <p className="mt-3 text-sm text-status-error">{error}</p>}

      {qrSvg && (
        <div className="space-y-4">
          <div
            className="inline-block rounded-control border border-border bg-white p-3"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
          <div className="text-xs text-app-muted">
            Can't scan? Type this secret manually: <code className="rounded bg-white/5 px-1 py-0.5 font-mono">{secret}</code>
          </div>
          <form onSubmit={verify} className="flex items-end gap-3">
            <label className="block text-sm flex-1">
              <span className="block text-app-muted">6-digit code</span>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
              />
            </label>
            <Button type="submit">Verify</Button>
          </form>
        </div>
      )}
    </Card>
  )
}

function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  return (
    <Card className="border-vip-gold/30 bg-vip-goldMute/30 p-6">
      <header className="mb-3">
        <h2 className="text-lg font-semibold text-vip-goldSoft">Recovery codes</h2>
        <p className="text-sm text-app-muted">
          Save these somewhere safe — you'll need one if you lose your second factor. Each is single-use; this is the
          only time we'll show the plaintext.
        </p>
      </header>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm">
        {codes.map((c) => (
          <code key={c} className="rounded bg-white/5 px-2 py-1">{c}</code>
        ))}
      </div>
    </Card>
  )
}

function RegenerateRecoveryCard() {
  return (
    <Card className="p-6">
      <header className="mb-3">
        <h2 className="text-lg font-semibold">Recovery codes</h2>
        <p className="text-sm text-app-muted">
          Generate or regenerate eight one-shot recovery codes. New codes invalidate any previously-issued ones.
        </p>
      </header>
      <Button variant="ghost" onClick={() => router.post("/portal/mfa/enroll/recovery_codes")}>
        Generate recovery codes
      </Button>
    </Card>
  )
}

function csrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? (meta.getAttribute("content") || "") : ""
}
